import * as React from 'react';
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  Button,
  message,
  InputNumber,
  Select,
} from 'antd';
import { AddMoveableAssetModalStyle } from './AddMoveableAssetModal.styles';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import {
  AddMoveableAssetModalProps,
} from './MoveableAssets.utils';
import { useTranslation } from 'react-i18next';
import { addMoveableAsset } from './MoveableAssets.api';
import { getAddressHierarchyData } from '../AssignedDrivers/AssignedDrivers.api';

const AddMoveableAssetModal: React.FC<AddMoveableAssetModalProps> = (
  props: AddMoveableAssetModalProps,
) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const [stateOptions, setStateOptions] = React.useState<Array<{ value: string; label: string }>>(
    [],
  );
  const [stateLoading, setStateLoading] = React.useState<boolean>(false);

  const {
    classes,
    handleModalClose,
    isVisible,
    reloadView,
    moveableAssetFieldList,
  } = props;

  const { t } = useTranslation();

  const renderTextInput = (placeholder: string) => {
    return <Input placeholder={placeholder} />;
  };

  const renderNumberInput = (placeholder: string) => {
    return (
      <InputNumber
        placeholder={placeholder}
        style={{ width: '100%' }}
        min={0}
      />
    );
  };

  const renderBooleanInput = (placeholder: string) => {
    return (
      <Select placeholder={placeholder} allowClear>
        <Select.Option value="true">{t('True')}</Select.Option>
        <Select.Option value="false">{t('False')}</Select.Option>
      </Select>
    );
  };

  const renderStateSelect = (placeholder: string) => {
    return (
      <Select
        placeholder={placeholder}
        allowClear
        options={stateOptions}
        loading={stateLoading}
        showSearch
        optionFilterProp='label'
      />
    );
  };

  const renderDropdownInput = (
    placeholder: string,
    values: Array<{ label: any; value: any }> = [],
    multiselect?: boolean,
  ) => {
    return (
      <Select
        placeholder={placeholder}
        allowClear
        mode={multiselect ? 'multiple' : undefined}
        options={values}
        showSearch
        optionFilterProp='label'
      />
    );
  };

  const getFormItem = (fieldToShow: any, label: string) => {
    if (fieldToShow.column_id === 'state') {
      return renderStateSelect(label);
    }

    const ui = fieldToShow.formUIComponent || {};
    const uiType = ui.type || fieldToShow.type;

    switch (uiType) {
      case 'dropdown': {
        const values = Array.isArray(ui.values) ? ui.values : [];
        const multiselect = Boolean(ui.multiselect);
        return renderDropdownInput(label, values, multiselect);
      }
      case 'number':
        return renderNumberInput(label);
      case 'boolean':
        return renderBooleanInput(label);
      case 'text':
      case 'string':
      default:
        return renderTextInput(label);
    }
  };

  const fetchStateOptions = async () => {
    try {
      setStateLoading(true);
      const payload = { location_key: 'state' } as any;
      const response = await getAddressHierarchyData(payload);
      if (response?.isSuccess) {
        const options = (response.data || []).map((item: any) => ({
          value: item.code,
          label: `${item.name} - ${item.code}`,
        }));
        setStateOptions(options);
      }
    } finally {
      setStateLoading(false);
    }
  };

  React.useEffect(() => {
    fetchStateOptions();
  }, []);

  const onFinish = async (values: any) => {
    try {
      await form.validateFields();
    } catch (err) {
      message.error(err.message || t('Please check the errors'));
      return;
    }

    const body = {
      ...values,
    };

    setConfirmLoading(true);
    const response = await addMoveableAsset(body);

    if (response.isSuccess) {
      message.success(t('Moveable Asset successfully created'));
      handleModalClose();
      reloadView();
    } else {
      message.error(response?.errorMessage);
    }
    setConfirmLoading(false);
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={handleModalClose}
      maskClosable={false}
      footer={null}
      title={t('Add Moveable Assets')}
      width={1000}
    >
      <Form
        form={form}
        className={classes.formContainer}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        onFinish={onFinish}
      >
        <Row gutter={18}>
          {moveableAssetFieldList
            .filter((item) => item?.is_create)
            .map((item) => {
              const label = item.pretty_name;
              const fieldKey = item.column_id;
              return (
                <Col span={12} key={fieldKey}>
                  <Form.Item
                    label={t(label)}
                    name={fieldKey}
                    colon={false}
                    rules={[
                      {
                        required: true,
                        message: t('This field is required'),
                      },
                    ]}
                  >
                    {getFormItem(item, label)}
                  </Form.Item>
                </Col>
              );
            })}
        </Row>
      </Form>
      <Form.Item className={classes?.actionButtons}>
        <Button onClick={handleModalClose} className={classes?.cancelButton}>
          {t('Cancel')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={confirmLoading}
          onClick={() => form.submit()}
        >
          {t('Submit')}
        </Button>
      </Form.Item>
    </Modal>
  );
};

const mapStateToProps = ({ masterData }) => {
  const { moveable_asset_column_list } = masterData;
  return {
    moveableAssetFieldList: moveable_asset_column_list || [],
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: AddMoveableAssetModalStyle,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(AddMoveableAssetModal);
